import type {
  ActivityPage,
  ApiError,
  ApprovalView,
  AuthStatus,
  BudgetView,
  ChatResponse,
  CostReport,
  FeedbackRequest,
  FeedbackView,
  HistoryTurn,
  KillSwitchRequest,
  PushInfo,
  StatusView,
  TaskView,
} from '@alfred/shared';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';

export interface VideoLibrary {
  project: { id: string; name: string };
  /** Alfred can record the product's footage himself. */
  canRecord?: boolean;
  footage: {
    id: string;
    kind: 'presenter' | 'screen' | 'other';
    slot: string | null;
    description: string;
    durationSec: number | null;
    status: 'pending' | 'ready' | 'failed';
  }[];
}

export interface ApiKeyStatus {
  id: string;
  name: string;
  purpose: string;
  where: string;
  status: 'saved' | 'server' | 'missing';
  /** Last 4 characters only. */
  hint: string | null;
}

export interface PostIdea {
  platform: 'x' | 'reddit' | 'tiktok' | 'instagram';
  project: string;
  id: string;
  url: string;
  where: string;
  text: string;
  numbers: string;
  why: string | null;
}

export interface PostedItem {
  approvalId: string;
  platform: 'x' | 'reddit' | 'video';
  summary: string;
  postedAt: string;
  url: string | null;
  numbers: string | null;
  inspiration: { url: string; label: string } | null;
}

export interface VideoPlanView {
  project: string;
  id: string;
  title: string;
  createdAt: string;
  source: { url: string; label: string; numbers: string } | null;
  plan: unknown;
}

export interface PostsData {
  projects: { id: string; name: string }[];
  ideas: PostIdea[];
  posted: PostedItem[];
  plans: VideoPlanView[];
}

export type TodoPlatform = 'x' | 'reddit' | 'tiktok' | 'instagram' | 'video' | 'other';

export interface TodoItemView {
  id: string;
  text: string;
  platform: TodoPlatform;
  minutes: number | null;
  why: string | null;
  approvalId: string | null;
  /** A group of drafts ("Send your 10 X replies") and how many are handled. */
  approvalIds?: string[];
  progress?: { done: number; total: number };
  link: string | null;
  done: boolean;
  source: 'alfred' | 'anthony';
}

export interface TodosData {
  project: { id: string; name: string } | null;
  list: {
    date: string;
    summary: string | null;
    items: TodoItemView[];
    generatedAt: string;
  } | null;
  reddit: {
    username: string | null;
    active: boolean;
    karma: number | null;
    ageDays: number | null;
    goal: { karma: number; days: number };
  } | null;
}

export interface UpgradeView {
  id: string;
  title: string;
  status: 'approved' | 'done' | 'dropped';
  approvedAt: string;
  doneAt: string | null;
  note: string | null;
}

export interface SkillView {
  id: string;
  name: string;
  /** When Alfred uses it, in Anthony's words. */
  trigger: string;
  steps: string;
  project: string | null;
  uses: number;
  lastUsedAt: string | null;
}

export interface RoutinesView {
  schedules: {
    id: string;
    title: string;
    kind: 'task' | 'reminder';
    /** e.g. "Weekdays at 09:00". */
    when: string;
    nextRunAt: string | null;
    status: string;
    lastError: string | null;
  }[];
  monitors: {
    id: string;
    name: string;
    url: string;
    state: 'up' | 'down' | 'unknown';
    lastCheckedAt: string | null;
    lastError: string | null;
  }[];
}

export interface GoogleStatus {
  /** False until a Google app's client id and secret are set on the server. */
  configured: boolean;
  connectedAs: string | null;
}

export interface BriefingSettings {
  enabled: boolean;
  /** "HH:MM", Melbourne time. */
  time: string;
}

export interface LocationStatus {
  /** False when the server has no encryption key, so location is switched off. */
  available: boolean;
  deviceSetUp: boolean;
  lastUpdateAt: string | null;
}

export interface LocationSetup {
  url: string;
  username: string;
  /** The phone's token: shown once. */
  password: string;
  /** owntracks:///config?inline=… : opens OwnTracks with everything filled in. */
  configLink: string;
}

export class ApiFailure extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | undefined,
  ) {
    super(message);
    this.name = 'ApiFailure';
  }
}

/** Called when the server says the session is gone, so the app can show sign-in. */
let onUnauthenticated: () => void = () => {};
export function setUnauthenticatedHandler(fn: () => void) {
  onUnauthenticated = fn;
}

async function call<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const data = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const err = (data ?? {}) as Partial<ApiError>;
    if (res.status === 401 && err.code === 'unauthenticated') onUnauthenticated();
    throw new ApiFailure(err.error ?? `request failed (${res.status})`, res.status, err.code);
  }
  return data as T;
}

export const api = {
  authStatus: () => call<AuthStatus>('GET', '/api/auth/status'),

  async register(name: string, setupCode?: string) {
    const optionsJSON = await call<PublicKeyCredentialCreationOptionsJSON>(
      'POST',
      '/api/auth/register/options',
      setupCode ? { setupCode } : {},
    );
    const response = await startRegistration({ optionsJSON });
    await call('POST', '/api/auth/register/verify', {
      response,
      name,
      ...(setupCode ? { setupCode } : {}),
    });
  },

  async login() {
    const optionsJSON = await call<PublicKeyCredentialRequestOptionsJSON>(
      'POST',
      '/api/auth/login/options',
      {},
    );
    const response = await startAuthentication({ optionsJSON });
    await call('POST', '/api/auth/login/verify', { response });
  },

  logout: () => call('POST', '/api/auth/logout', {}),
  removePasskey: (id: string) => call('DELETE', `/api/auth/passkeys/${encodeURIComponent(id)}`),

  chat: (message: string, sessionId: string | null, spoken = false) =>
    call<ChatResponse>('POST', '/api/chat', {
      message,
      ...(sessionId ? { sessionId } : {}),
      ...(spoken ? { spoken } : {}),
    }),
  voiceInfo: () => call<{ enabled: boolean }>('GET', '/api/voice'),
  async transcribe(audio: Blob) {
    const res = await fetch('/api/voice/transcribe', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': audio.type || 'audio/webm' },
      body: audio,
    });
    const data = (await res.json().catch(() => null)) as { text?: string; error?: string } | null;
    if (!res.ok) throw new ApiFailure(data?.error ?? 'couldn’t hear that', res.status, undefined);
    return { text: data?.text ?? '' };
  },
  speak: (text: string) => call<{ id: string }>('POST', '/api/voice/speak', { text }),
  history: () => call<HistoryTurn[]>('GET', '/api/history?limit=100'),
  remembered: (turnIds: string[]) =>
    call<Record<string, string[]>>('GET', `/api/remembered?turns=${turnIds.join(',')}`),
  feedback: (turnId: string, body: FeedbackRequest) =>
    call<FeedbackView>('PUT', `/api/feedback/${turnId}`, body),
  approvals: () => call<ApprovalView[]>('GET', '/api/approvals'),
  /** With `input`, Anthony edited the request first (e.g. reworded a post). */
  approve: (id: string, input?: Record<string, unknown>) =>
    call<ApprovalView>('POST', `/api/approvals/${id}/approve`, input ? { input } : {}),
  reject: (id: string, note?: string) =>
    call<ApprovalView>('POST', `/api/approvals/${id}/reject`, note ? { note } : {}),
  status: () => call<StatusView>('GET', '/api/status'),
  activity: (filter: string, before?: string | null) =>
    call<ActivityPage>(
      'GET',
      `/api/activity?filter=${filter}&limit=50${before ? `&before=${encodeURIComponent(before)}` : ''}`,
    ),
  tasks: () => call<TaskView[]>('GET', '/api/tasks'),
  pushInfo: () => call<PushInfo>('GET', '/api/push'),
  pushSubscribe: (body: {
    subscription: { endpoint: string; keys: { p256dh: string; auth: string } };
    device: string;
  }) => call('POST', '/api/push/subscribe', body),
  pushUnsubscribe: (endpoint: string) => call('POST', '/api/push/unsubscribe', { endpoint }),
  pushTest: () => call<{ sent: number }>('POST', '/api/push/test', {}),
  budget: () => call<BudgetView | null>('GET', '/api/budget'),
  setBudget: (
    id: string,
    monthlyUsd: number | null,
    basis: string | null,
    onLimit?: 'pause' | 'ask' | 'warn',
  ) =>
    call<BudgetView | null>('PUT', `/api/budget/features/${encodeURIComponent(id)}`, {
      monthlyUsd,
      basis,
      ...(onLimit ? { onLimit } : {}),
    }),
  setFixedCost: (body: { id?: string; label: string; monthlyUsd: number }) =>
    call<BudgetView | null>('PUT', '/api/budget/fixed', body),
  removeFixedCost: (id: string) =>
    call<BudgetView | null>('DELETE', `/api/budget/fixed/${encodeURIComponent(id)}`),
  videoLibrary: (project?: string) =>
    call<VideoLibrary | null>(
      'GET',
      `/api/videos${project ? `?project=${encodeURIComponent(project)}` : ''}`,
    ),
  recordFootage: (project?: string) =>
    call<{ started: boolean }>('POST', '/api/videos/record', project ? { project } : {}),
  /** Sends a recording as-is (no JSON), with what it shows in the query. */
  async uploadFootage(file: File, description: string, project?: string) {
    const params = new URLSearchParams({ description, ...(project ? { project } : {}) });
    const res = await fetch(`/api/videos/footage?${params}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': file.type || 'video/mp4' },
      body: file,
    });
    const data = (await res.json().catch(() => null)) as { error?: string; code?: string } | null;
    if (!res.ok)
      throw new ApiFailure(data?.error ?? `upload failed (${res.status})`, res.status, data?.code);
    return data as { id: string; durationSec: number };
  },
  videoFileUrl: (project: string, productionId: string) =>
    `/api/videos/${encodeURIComponent(project)}/${encodeURIComponent(productionId)}/file`,
  apiKeys: () => call<{ available: boolean; keys: ApiKeyStatus[] }>('GET', '/api/keys'),
  saveApiKey: (id: string, key: string) =>
    call<{ checked: boolean; keys: ApiKeyStatus[] }>('PUT', `/api/keys/${encodeURIComponent(id)}`, {
      key,
    }),
  removeApiKey: (id: string) =>
    call<{ keys: ApiKeyStatus[] }>('DELETE', `/api/keys/${encodeURIComponent(id)}`),
  routines: () => call<RoutinesView>('GET', '/api/schedules'),
  skills: () => call<{ skills: SkillView[] }>('GET', '/api/skills'),
  upgrades: () => call<{ upgrades: UpgradeView[] }>('GET', '/api/upgrades'),
  posts: () => call<PostsData>('GET', '/api/posts'),
  dismissIdea: (idea: { project: string; platform: PostIdea['platform']; id: string }) =>
    call('POST', '/api/posts/ideas/dismiss', idea),
  removePlan: (project: string, id: string) =>
    call('DELETE', `/api/posts/plans/${encodeURIComponent(project)}/${encodeURIComponent(id)}`),
  draftIdea: (idea: {
    project: string;
    platform: PostIdea['platform'];
    id: string;
    kind?: 'post' | 'reply';
  }) => call('POST', '/api/posts/ideas/draft', idea),
  todos: (project?: string) =>
    call<TodosData>('GET', `/api/todos${project ? `?project=${encodeURIComponent(project)}` : ''}`),
  todoItem: (body: { project: string; id?: string; done?: boolean; text?: string }) =>
    call('POST', '/api/todos/item', body),
  writeTodos: (project: string) => call('POST', '/api/todos/generate', { project }),
  redditAccount: (body: { project: string; karma?: number; createdOn?: string }) =>
    call('PUT', '/api/reddit/account', body),
  removeSkill: (id: string) => call('DELETE', `/api/skills/${encodeURIComponent(id)}`),
  cancelSchedule: (id: string) => call('POST', `/api/schedules/${id}/cancel`, {}),
  removeMonitor: (id: string) => call('POST', `/api/monitors/${id}/remove`, {}),
  googleStatus: () => call<GoogleStatus>('GET', '/api/google'),
  googleConnect: () => call<{ url: string }>('POST', '/api/google/connect', {}),
  googleDisconnect: () => call('DELETE', '/api/google'),
  briefingSettings: () => call<BriefingSettings | null>('GET', '/api/briefing'),
  setBriefing: (s: BriefingSettings) => call<BriefingSettings>('PUT', '/api/briefing', s),
  briefingNow: () => call<{ sent: boolean }>('POST', '/api/briefing/now', {}),
  locationStatus: () => call<LocationStatus>('GET', '/api/location'),
  locationSetup: () => call<LocationSetup>('POST', '/api/location/device', {}),
  locationRemove: () => call('DELETE', '/api/location/device'),
  costs: (days: number) => call<CostReport>('GET', `/api/costs?days=${days}`),
  killSwitch: (req: KillSwitchRequest) =>
    call<StatusView['killSwitch']>('POST', '/api/killswitch', req),
};

/** Turns WebAuthn's terse browser errors into something Anthony can act on. */
export function describeError(err: unknown): string {
  if (err instanceof ApiFailure) return err.message;
  if (err instanceof Error) {
    if (err.name === 'NotAllowedError') return 'Cancelled, or the passkey prompt timed out.';
    if (err.name === 'InvalidStateError') return 'This device already has a passkey for Alfred.';
    if (/push service error/i.test(err.message)) {
      // Brave ships with push messaging off; other browsers rarely hit this.
      return 'This browser’s push service is off. In Brave: brave://settings/privacy → turn on “Use Google services for push messaging”, restart Brave, then try again.';
    }
    return err.message;
  }
  return String(err);
}
